import React, { useState, useEffect } from 'react';
import { ArrowLeft, Play, Clock, Calendar, X, Film } from 'lucide-react';
import api from '../lib/api';

const VideoEmbed = ({ video }) => {
  const source = video.video_source || (video.youtube_video_id ? 'youtube' : 'unknown');
  const videoId = video.video_id || video.youtube_video_id;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  if (source === 'youtube') {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&fs=1`}
        title={video.title}
        className="absolute inset-0 w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        referrerPolicy="no-referrer"
      />
    );
  }

  if (source === 'drive') {
    // On mobile, Drive's /preview iframe is unreliable (black overlay, broken
    // controls). Show a fallback CTA that opens the Drive viewer in a new tab,
    // where playback works correctly. Download stays disabled at the file level.
    if (isMobile) {
      const driveUrl = `https://drive.google.com/file/d/${videoId}/view`;
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#1a2332] to-[#0f172a] text-white p-6 text-center">
          <Play className="w-12 h-12 text-[#c9a227]" strokeWidth={1.5} />
          <p className="text-sm text-white/85">El reproductor embebido no es compatible en algunos móviles.</p>
          <a
            href={driveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-5 py-2.5 rounded-full text-sm font-medium"
            data-testid="video-open-fullscreen"
          >
            Ver vídeo en pantalla completa
          </a>
          <p className="text-[10px] text-white/40">Se abrirá en una pestaña nueva</p>
        </div>
      );
    }
    return (
      <iframe
        src={`https://drive.google.com/file/d/${videoId}/preview`}
        title={video.title}
        className="absolute inset-0 w-full h-full"
        width="100%"
        height="100%"
        frameBorder="0"
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture; accelerometer; gyroscope"
        allowFullScreen
        loading="eager"
      />
    );
  }

  return <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white text-sm">Formato de vídeo no soportado</div>;
};

const VideoThumbnail = ({ video }) => {
  const [driveFailed, setDriveFailed] = useState(false);
  const source = video.video_source || (video.youtube_video_id ? 'youtube' : 'unknown');
  const videoId = video.video_id || video.youtube_video_id;

  // Priority 1: Custom thumbnail URL (from admin)
  if (video.thumbnail_url) {
    return (
      <img
        src={video.thumbnail_url}
        alt={video.title}
        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
        loading="lazy"
      />
    );
  }

  // Priority 2: YouTube auto-thumbnail
  if (source === 'youtube') {
    return (
      <img
        src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
        alt={video.title}
        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
        loading="lazy"
      />
    );
  }

  // Priority 3: Google Drive auto-thumbnail (try loading, fallback to placeholder)
  if (source === 'drive' && !driveFailed) {
    return (
      <img
        src={`https://drive.google.com/thumbnail?id=${videoId}&sz=w640`}
        alt={video.title}
        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
        loading="lazy"
        onError={() => setDriveFailed(true)}
      />
    );
  }

  // Fallback: styled placeholder
  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
      <div className="text-center">
        <Film className="w-10 h-10 text-gray-500 mx-auto mb-1" />
        <span className="text-xs text-gray-500">Google Drive</span>
      </div>
    </div>
  );
};

const ClassVideos = ({ courseSlug, onBack }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await api.get(`/special-courses/${courseSlug}/class-videos`);
        setVideos(res.data.videos || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [courseSlug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#D4A84B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="class-videos-section">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#D4A84B] mb-6 transition-colors" data-testid="back-to-course-btn">
        <ArrowLeft className="w-4 h-4" />
        Volver al curso
      </button>

      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">Clases en Vídeo</h1>
        <div className="h-0.5 w-16 bg-[#D4A84B] mt-2" />
        <p className="text-sm text-gray-400 mt-2">{videos.length} {videos.length === 1 ? 'clase grabada' : 'clases grabadas'}</p>
      </div>

      {/* Active Video Player */}
      {activeVideo && (
        <div className="mb-8" data-testid="video-player">
          <div className="bg-black rounded-xl overflow-hidden shadow-xl" onContextMenu={e => e.preventDefault()}>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <VideoEmbed video={activeVideo} />
            </div>
          </div>
          <div className="mt-4 flex items-start justify-between">
            <div>
              <h2 className="text-lg font-heading text-[hsl(var(--text-primary))]">{activeVideo.title}</h2>
              <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                {activeVideo.class_date && (
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(activeVideo.class_date)}</span>
                )}
                {activeVideo.duration && (
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{activeVideo.duration}</span>
                )}
              </div>
              {activeVideo.description && <p className="text-sm text-gray-500 mt-2">{activeVideo.description}</p>}
            </div>
            <button onClick={() => setActiveVideo(null)} className="text-gray-400 hover:text-gray-600 p-1" data-testid="close-player-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Video Grid */}
      {videos.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
            <Film className="w-7 h-7 text-gray-300" />
          </div>
          <p className="text-gray-400">Aún no hay clases grabadas</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="video-grid">
          {videos.filter(v => v.id !== activeVideo?.id).map(video => (
            <div
              key={video.id}
              onClick={() => { setActiveVideo(video); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="group text-left bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-lg hover:border-[#D4A84B]/30 transition-all duration-300 cursor-pointer"
              data-testid={`video-card-${video.id}`}
            >
              <div className="relative aspect-video bg-gray-900 overflow-hidden">
                <VideoThumbnail video={video} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-[#D4A84B]/90 flex items-center justify-center group-hover:bg-[#D4A84B] group-hover:scale-110 transition-all duration-300 shadow-lg">
                    <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
                  </div>
                </div>
                {video.duration && (
                  <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">{video.duration}</span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-medium text-sm text-[hsl(var(--text-primary))] group-hover:text-[#D4A84B] transition-colors line-clamp-1">{video.title}</h3>
                {video.class_date && (
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{formatDate(video.class_date)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClassVideos;
